import { matchPath } from 'react-router-dom';
import useRequestsTable from '@common/hooks/use-requests-table';
import { AccessRequestsScopeModel, AccessStatus, useGetUserSession, useIsMfaEnrolled } from '@apono/client-app-sdk';
import { useEffect, useMemo, useState } from 'react';
import { EnterMfaBanner } from '@common/organisms/EnterMfaBanner';
import { SetUpMfaBanner } from '@common/organisms/SetUpMfaBanner';
import routes from '@routes';
import { useAponoAuth } from '@common/ui/Authenticated';

export function MfaBanners() {
  const { data: userSession } = useGetUserSession();

  if (!userSession?.settings.mfa_enabled_for_account) {
    return;
  }

  return <HandleMfaBanners />;
}

function useMfaEnrolled() {
  const { isMfaEnrolled } = useAponoAuth();
  const [enabledMfaEnrolled, setEnabledMfaEnrolled] = useState(() => !isMfaEnrolled);

  const {
    data,
    isFetched: isMfaFetched,
    error,
  } = useIsMfaEnrolled({
    refetchInterval: 60 * 1000,
    enabled: enabledMfaEnrolled,
  });

  const isFetched = useMemo(
    () => (enabledMfaEnrolled ? isMfaFetched && !error && !!data : true),
    [enabledMfaEnrolled, isMfaFetched, error, data],
  );

  const isEnrolled = useMemo(
    () => isFetched && (data?.is_mfa_enrolled || isMfaEnrolled),
    [data, isFetched, isMfaEnrolled],
  );

  useEffect(() => {
    if (isEnrolled && isFetched && enabledMfaEnrolled) {
      setEnabledMfaEnrolled(false);
    }
  }, [enabledMfaEnrolled, isEnrolled, isFetched]);

  return {
    isFetched,
    isEnrolled,
  };
}

function HandleMfaBanners() {
  const { isFetched, isEnrolled } = useMfaEnrolled();

  if (!isFetched) {
    return;
  }

  if (!isEnrolled) {
    return <SetUpMfaBanner />;
  }

  const isInOpenRequestsPage = matchPath({ path: routes.OpenRequests.path }, window.location.pathname);
  if (!isInOpenRequestsPage) {
    return;
  }

  return <HandleEnterMfaBanner />;
}

function HandleEnterMfaBanner() {
  const { pageData } = useRequestsTable({
    statuses: [AccessStatus.PendingMFA],
    scope: AccessRequestsScopeModel.MyRequests,
    refetch: 5000,
  });

  const hasPendingMfaRequests = useMemo(() => pageData?.data.length && pageData?.data.length > 0, [pageData]);
  if (!hasPendingMfaRequests) {
    return;
  }

  return <EnterMfaBanner requestCount={pageData?.pagination.total || 0} variant="multiple" />;
}
